import { get } from "lodash";
import React, { Component } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Container, Header } from "semantic-ui-react";

import UnauthorisedFeature from "../../components/auth/UnauthorisedFeature.jsx";
import Entity404 from "../../components/entity404/Entitiy404.jsx";
import SimplePreloader from "../../components/preloaders/SimplePreloader.jsx";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs.jsx";
import ActionDropdownMenu from "../../components/actionDropdownMenu/ActionDropdownMenu.jsx";
import ResourceMarker from "../../components/marking/ResourceMarker.jsx";
import ProjectDetailsPanel from "../../components/projects/projectDetailsPanel/ProjectDetailsPanel.jsx";
import ProjectResourcesPanel from "../../components/projects/ProjectResourcesPanel.jsx";
import UsagePanel from "../../components/usage/UsagePanel.jsx";
import ConfirmationModal from "../../components/modals/ConfirmationModal.jsx";
import ChallengeConfirmationModal from "../../components/modals/ChallengeConfirmationModal.jsx";
import MarkingMessage from "../../components/marking/MarkingMessage.jsx";

import { AT_TYPES, RESOURCE_URI_CHUNKS } from "../../constants/entity.js";
import { PROJECTS } from "../../state/actions/actionConstants.js";
import { OPERATIONS } from "../../constants/operations.js";
import { ROUTES } from "../../constants/routing.js";

import { getResourceFromProjectByAtId } from "../../utils/utils.js";
import { getDisabledStateFromMarking } from "../../utils/marking.js";

import { getProject } from "../../state/reducers/projects/projectsActions.js";
import {
  operationAction,
  operationActionByEntity
} from "../../state/reducers/operations/operationActions.js";

import projectSelector from "../../state/reducers/projects/projectSelector.js";
import endpointSelector from "../../state/reducers/endpoints/endpointSelector.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class Project extends Component {
  static propTypes = {
    data: PropTypes.object,
    isFetching: PropTypes.bool.isRequired,
    listProjectAvailability: PropTypes.object.isRequired,
    copy: PropTypes.func.isRequired,

    getProject: PropTypes.func.isRequired,
    operationAction: PropTypes.func.isRequired,
    operationActionByEntity: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);

    this.state = {
      atId: `/${RESOURCE_URI_CHUNKS.PROJECTS}/${props.match.params.id}`,
      markingData: {},
      marking: null,

      showModal: false,
      modalCopyKey: "",
      modalCopy: {},
      modalData: {},
      modalAction: null
    };
  }

  static getDerivedStateFromProps(props, state) {
    const marking = get(props, "data.marking", null);

    if (marking !== state.marking) {
      const markingData = getDisabledStateFromMarking(marking);
      return { marking, markingData };
    }

    return null;
  }

  componentDidMount = () => {
    const { getProject, listProjectAvailability } = this.props;
    if (listProjectAvailability.available) getProject(this.state.atId);
  };

  // -- HANDLERS & ACTIONS -- //

  handleStartStopClick = (id, name) => {
    const { data, operationActionByEntity } = this.props;
    const resource = getResourceFromProjectByAtId(data, id);
    operationActionByEntity(resource, name);
  };

  doEdit = values => {
    const { data, operationAction } = this.props;
    operationAction(data, OPERATIONS.UPDATE, values);
  };

  handleDelete = (id, type) => {
    const { copy } = this.props;

    const typeCopyKey = type === AT_TYPES.PROJECT ? "projects" : "resource";

    const modalCopyKey = `${typeCopyKey}.modals.delete_modal`;
    const modalCopy = copy(modalCopyKey);
    const modalData = { id, type };
    const modalAction = this.doDelete;

    this.setState(
      {
        modalCopyKey,
        modalCopy,
        modalData,
        modalAction
      },
      this.openModal
    );
  };

  doDelete = () => {
    const { data, operationAction, operationActionByEntity } = this.props;
    const { id, type } = this.state.modalData;

    if (type === AT_TYPES.PROJECT) {
      operationAction(data, OPERATIONS.DELETE);
    } else {
      const resource = getResourceFromProjectByAtId(data, id);
      operationActionByEntity(resource, OPERATIONS.DELETE);
    }
  };

  // -- MODAL -- //

  openModal = () => {
    this.setState({ showModal: true });
  };

  closeModal = confirmed => {
    const { modalAction } = this.state;

    if (confirmed === true) modalAction();

    this.setState({ showModal: false });
  };

  // -- RENDER -- //

  getBreadcrumbSections = () => {
    const { copy, data } = this.props;

    return [
      { content: copy("navigation.projects"), href: ROUTES.PROJECTS },
      { content: get(data, "name", "Unknown"), active: true }
    ];
  };

  renderConfirmationModal = () => {
    const {
      modalData: { id, type },
      modalCopyKey,
      modalCopy,
      showModal
    } = this.state;
    const { data } = this.props;

    if (!showModal) return null;

    if (type === AT_TYPES.PROJECT) {
      return (
        <ChallengeConfirmationModal
          key={data["@id"]}
          open={showModal}
          entity={data}
          onClose={this.closeModal}
          copyKey={modalCopyKey}
        />
      );
    }

    const resource = getResourceFromProjectByAtId(data, id);

    return (
      <ConfirmationModal
        key={data["@id"]}
        open={showModal}
        entity={resource}
        onClose={this.closeModal}
        copy={modalCopy}
      />
    );
  };

  render() {
    const { atId, markingData } = this.state;
    const {
      data,
      isFetching,
      listProjectAvailability: { permitted, enabled },
      copy
    } = this.props;

    if (!permitted) {
      return <UnauthorisedFeature title={copy("projects.title")} />;
    }

    // early return if project isn't ready to render
    if (isFetching) {
      return <SimplePreloader />;
    }

    if (!enabled || !data || !data["@id"]) {
      return <Entity404 id={atId} type={AT_TYPES.PROJECT} />;
    }

    return (
      <Container>
        <Breadcrumbs sections={this.getBreadcrumbSections()} />

        <ActionDropdownMenu
          data={data}
          onDelete={this.handleDelete}
          copy={copy("project_dropdown_menu")}
        />

        <Header as="h3" className="action-dropdown-header">
          {get(data, "name", "Unknown")}
        </Header>

        <ResourceMarker marking={data.marking} className="project-marker" />

        <MarkingMessage
          type={MarkingMessage.PROJECT}
          message={markingData.message}
        />

        <ProjectDetailsPanel
          data={data}
          doEdit={this.doEdit}
          disabled={false}
        />

        <UsagePanel data={data} disabled={false} />

        <ProjectResourcesPanel
          project={data}
          handleStartStopClick={this.handleStartStopClick}
          handleDelete={this.handleDelete}
        />

        {this.renderConfirmationModal()}
      </Container>
    );
  }
}

const mapStateToProps = (state, props) => ({
  data: projectSelector.selectProject()(state, props),
  isFetching: state.projects.isFetching,
  listProjectAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.PROJECT,
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getProject: id => dispatch(getProject(id)),
  operationAction: (entity, operationName, data, options) =>
    dispatch(operationAction(PROJECTS, entity, operationName, data, options)),
  operationActionByEntity: (entity, operarationName, data, options) =>
    dispatch(operationActionByEntity(entity, operarationName, data, options))
});

export default connect(mapStateToProps, mapDispatchToProps)(Project);
